/**
 * CompletionPhase — Phase 6 (terminal).
 *
 * Renders when backendPhase === 'completed'. The workflow has
 * already signalled `markStepCompleted(10)` (handled by
 * useResidualProgress); this surface is purely informational.
 *
 * Three sections:
 *   1. Header — "Complete" badge + summary blurb.
 *   2. Metric grid — total controls / requirements / attack paths /
 *      risks.
 *   3. Treatment breakdown — counts per decision category.
 *
 * Plus a "Next steps" hint pointing the user at the rail to revisit
 * any phase, and at the Reports surface for exports (which already
 * live as standalone pages — no need to duplicate buttons here).
 */
import React, { useMemo } from 'react';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription } from '../../../../../components/ui/Empty';
import { TypographyH4, TypographyMuted } from '../../../../../components/ui/Typography';
import StatusPill from '../../../../../components/ui/StatusPill/StatusPill';
import type { ResidualActions } from '../../../hooks/useResidualProgress';
import type { ResidualState } from '../../../state/types';
import styles from './CompletionPhase.module.css';

interface CompletionPhaseProps {
  state: ResidualState;
  actions: ResidualActions;
}

export default function CompletionPhase({ state }: CompletionPhaseProps) {
  const data = state.data;

  const metrics = useMemo(() => {
    const controls = data?.controls ?? [];
    const requirements = controls.flatMap((c) => c.requirements ?? []);
    const attackPaths = data?.attack_paths ?? [];
    const risks = data?.risks ?? [];

    const treatmentCounts = { Avoid: 0, Reduce: 0, Share: 0, Retain: 0 };
    for (const risk of risks) {
      const t = risk.current_treatment;
      if (t === 'Avoid' || t === 'Reduce' || t === 'Share' || t === 'Retain') {
        treatmentCounts[t] += 1;
      }
    }

    let implementedControls = 0;
    for (const c of controls) {
      if (c.implementation_status === 'implemented') implementedControls += 1;
    }

    return {
      totalControls: controls.length,
      implementedControls,
      totalRequirements: requirements.length,
      totalAttackPaths: attackPaths.length,
      totalRisks: risks.length,
      treatmentCounts,
    };
  }, [data]);

  if (!data) {
    return (
      <div className={styles.phase}>
        <Empty>
          <EmptyHeader>
            <EmptyTitle>No completion data</EmptyTitle>
            <EmptyDescription>No completion data available.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={styles.phase}>
      <header className={styles.header}>
        <TypographyH4 className={styles.title}>
          Residual TARA <StatusPill tone="success" label="Complete" size="sm" />
        </TypographyH4>
        <TypographyMuted className={styles.subtitle}>
          All residual risks have been evaluated against the implemented
          controls and a treatment decision recorded per ISO 21434.
          Use the rail on the left to revisit any phase, or open the
          Reports page from the project nav for DOCX / PDF / CSV
          exports.
        </TypographyMuted>
      </header>

      {/* ===== Metric grid ===== */}
      <div className={styles.metricGrid}>
        <Metric
          label="Controls"
          value={metrics.totalControls}
          sub={
            metrics.totalControls > 0
              ? `${metrics.implementedControls} implemented`
              : undefined
          }
        />
        <Metric label="Requirements" value={metrics.totalRequirements} />
        <Metric label="Attack Paths" value={metrics.totalAttackPaths} />
        <Metric label="Risks" value={metrics.totalRisks} />
      </div>

      {/* ===== Treatment breakdown ===== */}
      {metrics.totalRisks > 0 && (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Treatment Breakdown</h3>
          <p className={styles.sectionDesc}>
            How the {metrics.totalRisks} residual risk
            {metrics.totalRisks === 1 ? ' was' : 's were'} treated.
            Reduce decisions trigger a workflow loop-back; Avoid /
            Share / Retain finalise the risk.
          </p>
          <div className={styles.treatmentRow}>
            <TreatmentItem
              label="Avoid"
              count={metrics.treatmentCounts.Avoid}
              tint="treatmentAvoid"
            />
            <TreatmentItem
              label="Reduce"
              count={metrics.treatmentCounts.Reduce}
              tint="treatmentReduce"
            />
            <TreatmentItem
              label="Share"
              count={metrics.treatmentCounts.Share}
              tint="treatmentShare"
            />
            <TreatmentItem
              label="Retain"
              count={metrics.treatmentCounts.Retain}
              tint="treatmentRetain"
            />
          </div>
        </section>
      )}

      {/* ===== Next steps ===== */}
      <div className={styles.hintCard}>
        <h3 className={styles.hintTitle}>Next steps</h3>
        <ul className={styles.hintList}>
          <li>
            Open <strong>Reports</strong> from the project nav to download
            the full TARA report (DOCX / PDF / CSV) including the
            residual decisions documented here.
          </li>
          <li>
            Use the rail on the left to revisit any phase&mdash;all
            content is read-only after completion.
          </li>
        </ul>
      </div>
    </div>
  );
}

/* ============================================================
   Small presentational helpers.
   ============================================================ */
function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: number;
  sub?: string;
}) {
  return (
    <div className={styles.metricCard}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
      {sub && <span className={styles.metricSub}>{sub}</span>}
    </div>
  );
}

function TreatmentItem({
  label,
  count,
  tint,
}: {
  label: string;
  count: number;
  tint: 'treatmentAvoid' | 'treatmentReduce' | 'treatmentShare' | 'treatmentRetain';
}) {
  return (
    <div className={`${styles.treatmentItem} ${styles[tint]}`}>
      <span className={styles.treatmentLabel}>{label}</span>
      <span className={styles.treatmentCount}>{count}</span>
    </div>
  );
}
